import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Notice } from '@/components/ui/Notice';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import {
  SegmentedControl,
  type SegmentedControlOption,
} from '@/components/ui/SegmentedControl';
import {
  PROJECTS_OF_INTEREST_LABEL,
  PROJECTS_OF_INTEREST_NOUN,
  companyProjectsLabel,
  projectOfInterestOptions,
  type SelectableProject,
} from '@/lib/company-projects';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  type CycleScopeIdIndex,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';

/**
 * A company's PROJECTS OF INTEREST, edited in a dialog — the University
 * Partnerships dashboard's editing surface for the column `/companies` and
 * `/companies/:id` edit in place.
 *
 * Owner's request, 2026-09-12: the UP dashboard's cell shows the project names
 * and the whole cell opens this.
 *
 * ## ⚠️ THIS IS NOT A FOURTH COPY OF THE COLUMN'S RULES
 *
 * Every decision about *what may be offered*, *what a stored project looks
 * like* and *what the cell says* lives in `src/lib/company-projects.ts` and
 * `src/lib/project-cycle-scope.ts`. Read both headers before changing anything
 * here; this file is a dialog around them and owns exactly three things the
 * modules cannot: the DRAFT, the cycle pill's per-dialog state, and when it is
 * honest to call {@link CompanyProjectsModalProps.onSave}.
 *
 * ⚠️ **The UP dashboard is documented as rendering this column READ-ONLY**, and
 * that sentence in `company-projects.ts` is about the two dashboard CARDS —
 * cells in a scannable report, where a picker in the row would be a control
 * whose only outcome is a discarded change. The owner has since asked for an
 * editing surface there, and a dialog is the shape that does not put a picker
 * into a report row. **The read-only rule still binds the cards themselves**:
 * nothing here may be lifted back into a cell.
 *
 * ## ⛔ NOTHING HERE FETCHES, AND NOTHING HERE MAY
 *
 * `projectOptions` and `cycleScopes` arrive as props because `/projects` and
 * `/cycles` must be read **once per page**, never once per dialog open. A hook
 * with a fetch behind it would put one request per row-open against a shared
 * per-IP budget — and the failure is not an error anybody sees, it is other
 * people's requests starting to 429 halfway across the app.
 *
 * ⚠️ *SUPERSEDED: "an office-wide 120/60s budget."* It is **12,000/60s**
 * (corrected 2026-09-17). Reading once per page is still right — a per-row
 * lookup is the wrong shape whatever the ceiling, and the 429 storm that
 * redirected everybody to `/login` really happened — but the arithmetic that
 * reason cited is off by a factor of a hundred.
 *
 * ## ⛔ THERE IS NO PROSE IN THIS DIALOG
 *
 * A general explanation attached to a control is what the no-helper-text rule
 * names, and the owner asked for the consequence sentence that once sat here to
 * go. Projects of interest are a list on the company and create or close no
 * sales pursuit (owner's decision 2026-10-12), so there is no consequence left to
 * explain either. What earns a place beside a control here is a refusal
 * (the `error` banner), a why-this-is-disabled
 * ({@link CompanyProjectsModalProps.disabledReason}) and a fact about the data
 * (the `footnote`, which reports the `hasMore` truncation) — nothing that
 * explains a consequence.
 *
 * ## 🔴 THE WRITE IS A WHOLE-SET REPLACE, WHICH DECIDES THE DIALOG'S SHAPE
 *
 * ```
 * absent    leaves the set alone
 * []        CLEARS every project of interest
 * [1, 7]    REPLACES the whole set
 * ```
 *
 * So **opening and cancelling must send nothing at all** — not an empty array,
 * not the set that was passed in. `onSave` is called from exactly one place
 * ({@link save}), only from the Save button, and only when the draft differs
 * from what arrived. `[]` is therefore reachable only by somebody removing the
 * last chip from a set that had one, which is the structural answer
 * `changedGroupIds()` gives for user groups and `company-projects.ts` asks for
 * by name.
 *
 * ⚠️ **Difference is compared by CONTENT and order-independently.** The server
 * reads a set back in the project master's own `sortOrder` and a draft holds
 * them in tick order, so comparing as sequences would report **every** set as
 * changed — which would turn every Cancel-shaped interaction into a live write
 * of a set nobody edited. See {@link sameSet}.
 */
export interface CompanyProjectsModalProps {
  open: boolean;
  onClose: () => void;
  /** The company whose set this is. Null closes/renders nothing. */
  companyId: number | null;
  /** For the title, so the dialog names the record. */
  companyName: string;
  /** What the row currently holds. */
  projects: readonly { id: number; name: string }[];
  /** Every ACTIVE project, UNNARROWED by cycle — the page fetches once and passes it down. */
  projectOptions: readonly SelectableProject[];
  /** Both cycle scopes' id sets; the page fetches `/cycles` once and passes it down. */
  cycleScopes: CycleScopeIdIndex;
  optionsLoading: boolean;
  /** True when `/projects` returned fewer than it holds — render the footnote. */
  optionsTruncated: boolean;
  optionsFailed: boolean;
  /** False when the reader may not write companies; render read-only, never a disabled picker. */
  canEdit: boolean;
  disabledReason: string | undefined;
  /** Resolves when saved. The page owns the request and the row patch. */
  onSave: (
    companyId: number,
    projectIds: number[],
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
}

/**
 * The two pills, DERIVED from the shared union rather than written out — the
 * same constant `CompaniesPage` builds, for the same reason it states there:
 * the labels come from `PROJECT_CYCLE_SCOPE_LABELS`, which is also what
 * `projectOfInterestOptions` uses to explain why a stored project is pinned.
 * Spelling `Current` and `Previous` out here would be a second copy of a
 * vocabulary that already has one home, and the drift shows up as a picker
 * contradicting its own pins — a row hinted `Previous` sitting under a pill
 * that calls the same set something else. Mapping the union also means a third
 * scope appears on this control on its own.
 */
const PROJECT_CYCLE_SCOPE_OPTIONS: readonly SegmentedControlOption<ProjectCycleScope>[] =
  PROJECT_CYCLE_SCOPES.map((scope) => ({
    value: scope,
    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
  }));

/**
 * Membership, not order: the server owns the order it reads a set back in.
 *
 * ⚠️ **The same rule `InlineRegionsField` states for `regionIds`, written again
 * rather than imported, because that copy is not exported and reaching into a
 * sibling component for a helper couples a dialog to an inline editor.** Four
 * lines duplicated is cheaper than that coupling; the RULE has one home (this
 * file's header and `company-projects.ts`), which is the part that must not
 * drift.
 */
function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const value of right) {
    if (!left.has(value)) return false;
  }
  return true;
}

/**
 * The projects of interest dialog.
 *
 * ⚠️ **Declared at MODULE level, and every helper above it too.** A component
 * defined during a render is a new function identity on every render, so React
 * unmounts and remounts its whole subtree each time — here that would drop the
 * draft and reset the cycle pill on every keystroke in whatever list is behind
 * the dialog. `CompaniesPage` records the same rule for the same reason.
 */
export function CompanyProjectsModal({
  open,
  onClose,
  companyId,
  companyName,
  projects,
  projectOptions,
  cycleScopes,
  optionsLoading,
  optionsTruncated,
  optionsFailed,
  canEdit,
  disabledReason,
  onSave,
}: CompanyProjectsModalProps) {
  const storedValues = useMemo(
    () => projects.map((project) => String(project.id)),
    [projects],
  );

  const [draft, setDraft] = useState<string[]>([]);
  // The Current / Previous pill is remembered on the account (owner,
  // 2026-10-16), so it is no longer reset when the dialog reopens.
  const [scope, setScope] = useRememberedState<ProjectCycleScope>(
    'company-projects-cycle-scope',
    DEFAULT_PROJECT_CYCLE_SCOPE,
    (raw) => PROJECT_CYCLE_SCOPES.find((value) => value === raw),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * ── RE-SEEDING THE DRAFT, DURING RENDER RATHER THAN IN AN EFFECT ───────────
   *
   * 🔴 **A dialog reopened on a different row must never show the previous
   * row's draft.** The write is a whole-set REPLACE, so a stale draft is not a
   * cosmetic glitch: press Save and one company's projects of interest are
   * written onto another's, with a `200` and nothing on screen having said so.
   *
   * `openedFor` is the session this state belongs to — the company the dialog
   * is currently open on, or `null` when it is closed. Adjusting state during
   * render is the documented React answer to "reset state when a prop changes":
   * React re-runs this component immediately, before anything is committed, so
   * the first painted frame is already correct. An effect would paint the
   * previous row's selection for a frame first, and — worse — is one dependency
   * array away from not running at all.
   *
   * ⚠️ **Keyed on `open` AND `companyId` together.** Either alone is wrong:
   * `companyId` alone misses reopening on the same company after a cancel (the
   * draft would survive the close), and `open` alone misses a page that swaps
   * the row under an open dialog.
   *
   * ⚠️ **`storedValues` is read here and is NOT a dependency of anything.** The
   * draft must follow the record only at the moment the dialog opens; following
   * it afterwards would silently discard ticks as soon as the page refreshed
   * the row for any unrelated reason.
   */
  const session = open ? companyId : null;
  const [openedFor, setOpenedFor] = useState<number | null>(null);
  if (openedFor !== session) {
    setOpenedFor(session);
    setDraft(storedValues);
    setError(null);
    /*
     * ⚠️ **Clearing `saving` here also makes a late `onSave` resolution
     * harmless, which is why no abort token is needed.** If somebody presses
     * Escape mid-save, this reset runs, and the promise then resolves into a
     * closed dialog: `setSaving(false)` is a no-op, and a `setError` lands on
     * state that the NEXT open's reset clears before anything renders. The
     * request itself is already in flight and is not ours to recall — the page
     * owns it, and it either wrote the set or it did not.
     */
    setSaving(false);
  }

  /*
   * ⚠️ **The pill decides what is OFFERED, never what is HELD.** `scope`
   * reaches `projectOfInterestOptions` and nothing else, so switching it cannot
   * touch `draft` — and that function pins every STORED project into every
   * scope, which is what stops a narrowed picker rendering blank over a real
   * value whose next save would then drop it.
   *
   * ⛔ **All four arguments are passed, positionally.** Omitting `cycleScopes`
   * would not be "the default scope" — it offers EVERY project, which is a
   * different list that looks identical on a screen where most projects happen
   * to be current. `company-projects.ts` says to read a missing third argument
   * as work outstanding rather than as a decision.
   *
   * ⚠️ **`projects` — the STORED set — is the second argument and must never be
   * `[]`.** A stored project the options omit renders as a chip labelled with
   * the raw id, and the next save drops it with a `200`.
   */
  const options = useMemo(
    () => projectOfInterestOptions(projectOptions, projects, cycleScopes, scope),
    [projectOptions, projects, cycleScopes, scope],
  );

  /*
   * ⚠️ **A failed list and a truncated list say different things**, and the
   * wording is `CompaniesPage`'s so the two surfaces over one column cannot
   * describe the same data differently.
   *
   * `optionsTruncated` is expected to fire on essentially every open: there are
   * 113 active projects against a `perPage` ceiling of 100, so roughly a dozen
   * are genuinely unreachable through this picker. **That is a fact about the
   * data, which is the footnote kind the no-helper-text rule keeps** — a picker
   * that is silently short is indistinguishable from a project that does not
   * exist.
   */
  const footnote = optionsFailed
    ? 'Project list unavailable. Reload to try again.'
    : optionsTruncated
      ? 'Not all projects are listed — this picker shows the first page of them.'
      : undefined;

  const dirty = !sameSet(draft, storedValues);

  /**
   * ⛔ **THE ONLY CALLER OF `onSave` IN THIS FILE.** Guarded on `dirty` so that
   * opening and saving without touching anything cannot re-send a set nobody
   * edited, and on `saving` so a double press cannot fire two replaces.
   */
  const save = async () => {
    if (companyId === null || !dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      /*
       * ⚠️ **No `.filter(Number.isFinite)` here, deliberately.** Every draft
       * value originates as `String(project.id)` from an option this dialog
       * built, so `Number` is exact — and if one ever were not, filtering it
       * away would silently SHORTEN a whole-set replace, which is a project of
       * interest deleted with a `200`. A `NaN` instead reaches the server as
       * `null` and comes back a 422 naming the key: loud, and recoverable.
       */
      const result = await onSave(
        companyId,
        draft.map((value) => Number(value)),
      );
      setSaving(false);
      if (result.ok) {
        onClose();
        return;
      }
      /*
       * 🔴 **A REFUSED SAVE KEEPS THE DIALOG OPEN WITH THE DRAFT INTACT.**
       * Closing on a refusal throws the person's selection away and leaves them
       * with a message about work they can no longer see, so the only recovery
       * is to reassemble the whole set from memory.
       */
      setError(result.message);
    } catch {
      /*
       * ⚠️ **The contract says `onSave` resolves rather than throws, so this is
       * a bug in the page — but an unhandled rejection would leave `saving`
       * true for ever, and a permanently disabled Save is a false claim that
       * there is nothing to save.** Fail visibly instead: this repo's rule is
       * that a control wrongly enabled costs one redundant request, where one
       * wrongly disabled is a lie with nobody watching.
       */
      setSaving(false);
      setError('Saving failed. Nothing was changed — try again.');
    }
  };

  /*
   * ⚠️ **Renders nothing without a company**, per the contract. `Modal` is
   * still handed `open` so its own focus-return and body-scroll-lock lifecycle
   * runs exactly as designed rather than being short-circuited by an early
   * `return null` here.
   */
  const hasCompany = companyId !== null;

  /*
   * A blank name would title the dialog with a dash. The record still needs
   * naming, so fall back to a phrase rather than to nothing.
   */
  const shownName = companyName.trim() === '' ? 'this company' : companyName.trim();

  return (
    <Modal
      open={open && hasCompany}
      onClose={onClose}
      /*
       * ⚠️ **The heading constant rather than a second spelling.** It is
       * singular because it names the QUESTION this data answers, and a dialog
       * over the same data answers the same question — `company-projects.ts`
       * keeps that word in one place precisely so one screen cannot start
       * saying "Projects" while another says "Pursuing".
       */
      title={`${PROJECTS_OF_INTEREST_LABEL} — ${shownName}`}
      size="md"
      /*
       * ⛔ **A half-made selection must not vanish to a clipped backdrop
       * click.** This is exactly what the flag exists for; Escape still closes,
       * which is a deliberate key press rather than a miss.
       */
      dismissOnBackdrop={false}
      footer={
        canEdit ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={save}
              loading={saving}
              /*
               * ⚠️ **Disabled when nothing changed, and that is a claim this
               * dialog can actually make**: the draft and the stored set are
               * both in hand and compared by content. It is the narrow case
               * `disabled` is reserved for — not a guess about the server.
               */
              disabled={saving || !dirty}
            >
              Save changes
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {error !== null && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}

        {canEdit ? (
          <>
            {/*
              ⚠️ The pills are ABOVE the picker and outside the `Field`: they
              choose which cycles the list offers, so they are a property of
              the list rather than a second control the label describes.
            */}
            <SegmentedControl
              /*
               * Names the CHOICE — it is the group's accessible name and is not
               * rendered visibly. Unlike `CompaniesPage`, which carries the
               * company here because twenty rows each mount one, a dialog has
               * exactly one and its title already names the record.
               */
              label="Project cycle"
              value={scope}
              options={PROJECT_CYCLE_SCOPE_OPTIONS}
              onChange={setScope}
            />

            <Field label={PROJECTS_OF_INTEREST_LABEL} hideLabel>
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  values={draft}
                  onChange={setDraft}
                  options={options}
                  loading={optionsLoading}
                  /*
                   * ⚠️ **NOT `maxVisibleChips={0}`.** That is the narrow-table-
                   * cell recipe — four chips in a ~190px cell wrap to four rows
                   * and take the grid row with them. This is a dialog with room
                   * for them, and chips are what let somebody see and remove an
                   * individual project rather than reopening a list to find out
                   * what a count is made of.
                   */
                  summaryNoun={PROJECTS_OF_INTEREST_NOUN}
                  placeholder={optionsLoading ? 'Loading projects…' : 'No projects'}
                  searchPlaceholder="Search projects…"
                  /*
                   * Select all is here on the owner's instruction (2026-09-13).
                   * The pursuit cost it was weighed against is gone — owner's
                   * decision 2026-10-12: projects of interest create and close
                   * no sales pursuit, they are a list on the company.
                   *
                   * ⚠️ The write is still a whole-set REPLACE diffed against
                   * the set this modal OPENED with, which is untouched by this
                   * prop — a Select all changes the DRAFT, never the payload
                   * shape, so the never-opened-the-picker case is still
                   * `undefined` rather than `[]`.
                   */
                  selectAll
                  /*
                   * ⚠️ **A failed list does NOT disable the picker.** Every
                   * stored project is still pinned into the options, so the
                   * reader can still remove one — and a disabled control over a
                   * failed fetch reads as a permission they do not have.
                   */
                  {...(footnote === undefined ? {} : { footnote })}
                  {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}
                />
              )}
            </Field>
          </>
        ) : (
          /*
           * ⚠️ **READ-ONLY IS TEXT, NEVER A DISABLED PICKER.** A greyed control
           * reads as something the reader has not earned the right to use and
           * says nothing about why; the names plus the reason say both.
           */
          <>
            {projects.length === 0 ? (
              <p className="m-0 text-sm text-fg-subtle">{companyProjectsLabel(projects)}</p>
            ) : (
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                {projects.map((project) => (
                  <li key={project.id}>
                    <Badge tone="neutral" variant="outline">{project.name.trim() === '' ? `#${project.id}` : project.name}</Badge>
                  </li>
                ))}
              </ul>
            )}
            {disabledReason !== undefined && (
              <p className="m-0 text-xs text-fg-muted">{disabledReason}</p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
